import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import OpenPlayToolsLayout from '../../components/openplay/OpenPlayToolsLayout';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabaseClient';

const encodeBase64Url = (obj) => {
  try {
    const json = JSON.stringify(obj);
    const utf8 = encodeURIComponent(json).replace(/%([0-9A-F]{2})/g, (_, p1) => String.fromCharCode(parseInt(p1, 16)));
    const base64 = btoa(utf8);
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  } catch (e) {
    return '';
  }
};

const sortOptions = [
  { value: 'updated-desc', label: '최근 수정순' },
  { value: 'created-desc', label: '최근 생성순' },
  { value: 'name-asc', label: '이름 오름차순' }
];

const BracketDashboard = () => {
  const navigate = useNavigate();
  const [brackets, setBrackets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const PAGE_SIZE = 15;
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    sort: 'updated-desc'
  });

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setLoadError('');
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const userId = sessionData?.session?.user?.id;
        if (!userId) {
          setLoadError('로그인이 필요합니다.');
          setBrackets([]);
          setTotalCount(0);
          return;
        }

        const from = (page - 1) * PAGE_SIZE;
        const to = from + PAGE_SIZE - 1;

        let query = supabase
          .from('brackets')
          .select('*', { count: 'exact' });

        if (filters.search) {
          query = query.ilike('name', `%${filters.search}%`);
        }

        if (filters.sort === 'name-asc') {
          query = query.order('name', { ascending: true });
        } else if (filters.sort === 'created-desc') {
          query = query.order('created_at', { ascending: false });
        } else {
          query = query.order('updated_at', { ascending: false });
        }

        const { data, error, count } = await query.range(from, to);

        if (error) {
          setLoadError(error?.message || '대진표 목록을 불러오지 못했습니다.');
          setBrackets([]);
          setTotalCount(0);
          return;
        }

        const mapped = (data || []).map((row) => {
          const participants = row?.participants || {};
          const selectedMemberIds = participants?.selectedMemberIds || [];
          const manualPlayers = participants?.manualPlayers || [];
          return {
            id: row?.id,
            name: row?.name,
            createdAt: row?.created_at,
            updatedAt: row?.updated_at,
            config: row?.config || {},
            result: row?.result || null,
            selectedMemberIds,
            manualPlayers
          };
        });

        setBrackets(mapped);
        setTotalCount(count || 0);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [page, filters]);

  useEffect(() => {
    setPage(1);
  }, [filters?.search, filters?.sort]);

  const filteredBrackets = useMemo(() => {
    let list = [...(brackets || [])];

    if (filters.search) {
      const q = filters.search.toLowerCase();
      list = list.filter(b => (b?.name || '').toLowerCase().includes(q));
    }

    list.sort((a, b) => {
      if (filters.sort === 'name-asc') {
        return (a?.name || '').localeCompare(b?.name || '');
      }
      if (filters.sort === 'created-desc') {
        return new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0);
      }
      return new Date(b?.updatedAt || b?.createdAt || 0) - new Date(a?.updatedAt || a?.createdAt || 0);
    });

    return list;
  }, [brackets, filters]);

  const totalPages = Math.max(1, Math.ceil((totalCount || 0) / PAGE_SIZE));

  const handleDelete = (id) => {
    const run = async () => {
      const ok = window.confirm('이 대진표를 삭제할까요?');
      if (!ok) return;

      const { error } = await supabase
        .from('brackets')
        .delete()
        .eq('id', id);

      if (error) {
        alert(error?.message || '삭제에 실패했습니다.');
        return;
      }

      setBrackets(prev => (prev || []).filter(b => b?.id !== id));
    };
    run();
  };

  const handleCopyShareUrl = async (b) => {
    const encoded = encodeBase64Url(b);
    if (!encoded) {
      alert('공유 링크 생성에 실패했습니다');
      return;
    }

    const url = `${window.location.origin}/bracket-share?data=${encoded}`;
    try {
      await navigator.clipboard.writeText(url);
      alert('공유 링크가 복사되었습니다');
    } catch (e) {
      window.prompt('공유 링크를 복사하세요', url);
    }
  };

  const handleOpenPublicPage = (b) => {
    const encoded = encodeBase64Url(b);
    if (!encoded) {
      alert('공개 페이지 생성에 실패했습니다');
      return;
    }
    const url = `${window.location.origin}/bracket-share?data=${encoded}`;
    window.open(url, '_blank');
  };

  return (
    <OpenPlayToolsLayout userRole="player">
      <main className="pt-6 md:pt-10 pb-[calc(84px+env(safe-area-inset-bottom,16px))] md:pb-6 px-4 max-w-6xl mx-auto">
        <div className="mb-6 md:mb-8">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-2">대진표 작성</h1>
          <p className="text-base md:text-lg caption text-muted-foreground">대진표를 생성하고 공유하세요</p>
        </div>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 md:mb-8">
          <div>
            <Button
              variant="default"
              iconName="Plus"
              iconPosition="right"
              onClick={() => navigate('/session-list-dashboard/bracket/new')}
            >
              새 대진표 만들기
            </Button>
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-3 md:p-6 mb-4 md:mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input
              label="검색"
              type="text"
              placeholder="대진표 이름"
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            />
            <Select
              label="정렬"
              options={sortOptions}
              value={filters.sort}
              onChange={(v) => setFilters(prev => ({ ...prev, sort: v }))}
            />
          </div>
        </div>

        {isLoading ? (
          <div className="bg-muted border border-border rounded-xl p-10 text-center">
            <p className="text-base font-medium text-foreground mb-2">대진표를 불러오는 중...</p>
            <p className="text-sm caption text-muted-foreground">잠시만 기다려주세요</p>
          </div>
        ) : loadError ? (
          <div className="bg-error/10 border border-error/20 rounded-xl p-10 text-center">
            <p className="text-base font-medium text-error mb-2">대진표를 불러올 수 없습니다</p>
            <p className="text-sm caption text-muted-foreground">{loadError}</p>
          </div>
        ) : filteredBrackets.length === 0 ? (
          <div className="bg-muted border border-border rounded-xl p-10 text-center">
            <Icon name="LayoutGrid" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <p className="text-base font-medium text-foreground mb-2">대진표가 없습니다</p>
            <p className="text-sm caption text-muted-foreground">"새 대진표 만들기"로 시작하세요</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
            {filteredBrackets.map((b) => {
              const roundCount = b?.result?.generated?.rounds?.length || 0;
              const playerCount =
                (b?.selectedMemberIds?.length || 0) + (b?.manualPlayers?.length || 0);

              return (
                <div key={b.id} className="bg-card border border-border rounded-xl p-4 md:p-6 hover-lift transition-athletic">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs caption text-muted-foreground">피클볼</p>
                      <h3 className="text-lg font-heading font-semibold text-foreground mt-1">{b?.name || '대진표'}</h3>
                      <p className="text-xs caption text-muted-foreground mt-1">
                        수정: {new Date(b?.updatedAt || b?.createdAt).toLocaleString('ko-KR')}
                      </p>
                    </div>
                    <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name="Swords" size={22} color="var(--color-accent)" />
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-3 gap-2">
                    <div className="bg-muted rounded-md border border-border p-2">
                      <p className="text-[11px] caption text-muted-foreground">인원</p>
                      <p className="text-sm font-medium text-foreground data-text">{playerCount}</p>
                    </div>
                    <div className="bg-muted rounded-md border border-border p-2">
                      <p className="text-[11px] caption text-muted-foreground">라운드</p>
                      <p className="text-sm font-medium text-foreground data-text">{roundCount}</p>
                    </div>
                    <div className="bg-muted rounded-md border border-border p-2">
                      <p className="text-[11px] caption text-muted-foreground">모드</p>
                      <p className="text-sm font-medium text-foreground">
                        {b?.config?.mode === 'rotation' ? '로테이션' : '팀 고정'}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <Button
                      variant="default"
                      iconName="Eye"
                      iconPosition="left"
                      onClick={() => handleOpenPublicPage(b)}
                      fullWidth
                    >
                      상세보기
                    </Button>
                    <Button
                      variant="outline"
                      iconName="Pencil"
                      iconPosition="left"
                      onClick={() => navigate(`/session-list-dashboard/bracket/${b.id}`)}
                      fullWidth
                    >
                      수정
                    </Button>
                    <Button
                      variant="outline"
                      iconName="Share2"
                      iconPosition="left"
                      onClick={() => handleCopyShareUrl(b)}
                      fullWidth
                    >
                      공유
                    </Button>
                    <Button
                      variant="outline"
                      iconName="Trash2"
                      iconPosition="left"
                      onClick={() => handleDelete(b.id)}
                      fullWidth
                    >
                      삭제
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

          {!isLoading && !loadError && totalPages > 1 && (
            <div className="mt-6 flex items-center justify-center gap-2">
              <Button
                variant="outline"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                이전
              </Button>
              <div className="text-sm caption text-muted-foreground">
                {page} / {totalPages}
              </div>
              <Button
                variant="outline"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                다음
              </Button>
            </div>
          )}

      </main>

    </OpenPlayToolsLayout>
  );
};

export default BracketDashboard;
